import type { AxiosRequestConfig } from "axios";
import api from "../../../utils/api";
import { unwrap, unwrapList } from "./apiEnvelope";

// ============================================================================
// Enlaces cortos y QR (épica #1518, issue #1522) — servicio de la consola.
//
// Habla con torre-de-control-api → módulo crmShortLinks:
//   • lista        GET  /crm/short-links
//   • crear        POST /crm/short-links            { label, channel, target, utm, product }
//   • editar       PUT  /crm/short-links/:code      { label, active, utm, target }
//   • estadísticas GET  /crm/short-links/:code/stats?days=30
//
// El enlace público es `<VITE_SHORT_LINK_BASE>/<code>` (rewrite de la
// landing a la API): manda al destino con `utm_*`, `ref=<code>` y `channel`, y
// cuenta el clic. Los PRESETS son las cinco opciones de WAYAK del funnel y la
// app de Movik (#1536, producto B2C que comparte plataforma con Wayak).
// ============================================================================

// La base es de cada tenant (variable de build `VITE_SHORT_LINK_BASE`, p. ej.
// `https://<landing>/r`); sin ella, la landing de WAYAK.
export const SHORT_LINK_BASE = String(import.meta.env.VITE_SHORT_LINK_BASE || "https://wayak-logistics.com/r").replace(/\/+$/, "");
/** La base sin esquema, para mostrarla («wayak-logistics.com/r»). */
export const SHORT_LINK_DISPLAY = SHORT_LINK_BASE.replace(/^https?:\/\//, "");

export const CHANNELS = ["landing", "whatsapp", "meta_ads", "qr", "pos", "flyer", "app_store", "referral", "other"] as const;
export type Channel = (typeof CHANNELS)[number];

export const CHANNEL_LABEL: Record<Channel, string> = {
  landing: "Landing",
  whatsapp: "WhatsApp",
  meta_ads: "Meta Ads",
  qr: "QR",
  pos: "Punto de venta",
  flyer: "Folleto / impreso",
  app_store: "Tienda de apps",
  referral: "Referido",
  other: "Otro",
};

export interface Utm {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
}

export interface ShortLink {
  code: string;
  label: string;
  channel: Channel;
  target: string;
  utm: Utm;
  product: string | null;
  createdBy: string | null;
  createdAt: unknown;
  updatedAt: unknown;
  clicks: number;
  lastClickAt: unknown;
  active: boolean;
  tenantCompanyId: string | null;
}

export interface ShortLinkInput {
  label: string;
  channel: Channel;
  target: string;
  utm?: Utm;
  product?: string | null;
}

export interface ShortLinkPatch {
  label?: string;
  active?: boolean;
  utm?: Utm;
  target?: string;
}

export interface ShortLinkStats {
  code: string;
  days: number;
  total: number;
  byDay: { date: string; clicks: number }[];
  clicks: number;
  lastClickAt: unknown;
}

/** Productos para los que se crean enlaces (valor guardado en `product`, en minúsculas). */
export const SHORT_LINK_PRODUCTS = [
  { value: "wayak", label: "Wayak Logistics (B2B)" },
  { value: "movik", label: "Movik (B2C)" },
] as const;
export type ShortLinkProduct = (typeof SHORT_LINK_PRODUCTS)[number]["value"];

/** App web de Movik (sitio propio verificado; está en la lista blanca de la API). */
export const MOVIK_APP_URL = "https://movik-production.web.app";

/**
 * Atajos por producto. WAYAK: las cinco opciones del funnel (ids estables,
 * iguales a la landing y al menú de WhatsApp). Movik: abrir/descargar la app
 * (todavía no hay una ruta pública para «Acompañar un trayecto»).
 */
export interface Preset {
  id: "demo" | "piloto" | "cuenta-gratis" | "comprar" | "vincular" | "movik-app";
  label: string;
  description: string;
  target: string;
  product: ShortLinkProduct;
}

export const PRESETS: readonly Preset[] = [
  { id: "demo", label: "Agendar demo", description: "Formulario de contacto de la landing", target: "https://wayak-logistics.com/#contacto", product: "wayak" },
  { id: "piloto", label: "Prueba piloto", description: "Solicitud de prueba piloto", target: "https://wayak-logistics.com/#/prueba-piloto", product: "wayak" },
  { id: "cuenta-gratis", label: "Crear cuenta gratis", description: "Alta self-service", target: "https://wayak-logistics.com/#/prueba", product: "wayak" },
  { id: "comprar", label: "Comprar dispositivo", description: "Tienda pública de WAYAK", target: "https://app.wayak-logistics.com/shop/wayak", product: "wayak" },
  { id: "vincular", label: "Vincular dispositivo", description: "Vincular un equipo ya comprado", target: "https://app.wayak-logistics.com/dispositivos/vincular", product: "wayak" },
  { id: "movik-app", label: "Descargar Movik", description: "Abrir la app de Movik", target: MOVIK_APP_URL, product: "movik" },
];

/** Los atajos de un producto (vacío si no tiene: entonces sólo «Otro destino»). */
export function presetsForProduct(product: string | null | undefined): Preset[] {
  const key = String(product ?? "").trim().toLowerCase();
  return PRESETS.filter((p) => p.product === key);
}

/** utm por omisión: `utm_source=<canal>`, `utm_medium=qr|link` y la opción como campaña. */
export function defaultUtm(channel: Channel, presetId?: Preset["id"] | null): Utm {
  return {
    source: channel,
    medium: channel === "qr" || channel === "flyer" || channel === "pos" ? "qr" : "link",
    ...(presetId ? { campaign: presetId } : {}),
  };
}

export const shortUrl = (code: string) => `${SHORT_LINK_BASE}/${code}`;

/** El destino con la atribución pegada, igual que lo arma la API (vista previa en la pantalla). */
export function previewTargetUrl(target: string, utm: Utm, code: string, channel: Channel): string {
  try {
    const url = new URL(target);
    const params: Record<string, string | undefined> = {
      utm_source: utm.source,
      utm_medium: utm.medium,
      utm_campaign: utm.campaign,
      utm_content: utm.content,
      utm_term: utm.term,
      ref: code,
      channel,
    };
    for (const [key, value] of Object.entries(params)) {
      if (value && !url.searchParams.has(key)) url.searchParams.set(key, value);
    }
    return url.toString();
  } catch {
    return target;
  }
}

// Los enlaces son del CRM (sellados con la compañía por la API), no de la
// compañía que el usuario tenga seleccionada en el encabezado.
const scoped = (signal?: AbortSignal, params?: Record<string, string | number>) =>
  ({ signal, params, _skipCompanyScope: true }) as AxiosRequestConfig;

export async function fetchShortLinks(signal?: AbortSignal): Promise<ShortLink[]> {
  return unwrapList<ShortLink>(await api.get("/crm/short-links", scoped(signal)));
}

export async function createShortLink(input: ShortLinkInput): Promise<ShortLink> {
  return unwrap<ShortLink>(await api.post("/crm/short-links", input, scoped()));
}

export async function updateShortLink(code: string, patch: ShortLinkPatch): Promise<ShortLink> {
  return unwrap<ShortLink>(await api.put(`/crm/short-links/${encodeURIComponent(code)}`, patch, scoped()));
}

export async function fetchShortLinkStats(code: string, days = 30, signal?: AbortSignal): Promise<ShortLinkStats> {
  return unwrap<ShortLinkStats>(await api.get(`/crm/short-links/${encodeURIComponent(code)}/stats`, scoped(signal, { days })));
}

// --- Errores ----------------------------------------------------------------

type ApiError = { response?: { status?: number; data?: { meta?: { code?: unknown; message?: unknown } } }; code?: string; name?: string };

/** Mensaje ya traducido por la API, o uno genérico. */
export function shortLinkErrorMessage(error: unknown, fallback = "No se pudo completar la operación."): string {
  const message = (error as ApiError)?.response?.data?.meta?.message;
  return typeof message === "string" && message.trim() ? message : fallback;
}

export function isCanceled(error: unknown): boolean {
  const e = error as ApiError;
  return e?.code === "ERR_CANCELED" || e?.name === "CanceledError" || e?.name === "AbortError";
}

/** Fecha corta en español, o «—». Acepta ISO o el timestamp serializado de Firestore. */
export function formatLinkDate(value: unknown, withTime = false): string {
  if (!value) return "—";
  let date: Date | null = null;
  if (typeof value === "string" || typeof value === "number") date = new Date(value);
  else if (typeof value === "object") {
    const seconds = (value as { _seconds?: number; seconds?: number })._seconds ?? (value as { seconds?: number }).seconds;
    if (typeof seconds === "number") date = new Date(seconds * 1000);
  }
  if (!date || Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
}
